<template>
  <div>
    <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
    <div v-else-if="isLoading">
      <LayupSpinner />
      <p class="text-black text-center">Loading...</p>
    </div>
    <div v-else class="max-w-md mx-auto bg-white rounded-xl">
      <WidgetMerchantLogo />
      <div class="flex justify-center mb-5">
        <img
          v-if="isRewardPercentage"
          src="https://res.cloudinary.com/layup/image/upload/v1759994337/percentage-reward_blw1rk.svg"
          alt="Percentage Reward"
        />
        <img
          v-else
          src="https://res.cloudinary.com/layup/image/upload/v1759994337/flat-reward_qjrnwt.svg"
          alt="Flat Reward"
        />
      </div>

      <div class="px-14 mb-3">
        <CurrencyInput
          v-model:modelValue="savingsGoal"
          label="Goal Amount"
          :maxLength="6"
          :allowCents="true"
          :min="minimumSavingsGoal"
          :max="boost?.merchant?.voucherSettings?.maximumSavingGoalInCents"
          :isLargeInput="true"
          :centerText="true"
          @update:is-valid="isAmountValid = $event"
        />
      </div>
      <div class="flex justify-center mb-5">
        <p class="text-grey-light-900 font-medium text-center">
          Reward amount
          <span class="text-boost-500 font-bold" data-testid="rewardAmount">
            {{ displayCurrency(rewardAmount) }}
            {{ isRewardPercentage ? `(${voucherSettings?.rewardPercentage}%)` : '' }}
          </span>
        </p>
      </div>

      <SelectInput
        :label="`Select monthly payment date`"
        :selections="monthlyBillingDays"
        v-model="billingDay"
        data-testid="billingDay"
      />

      <SelectInput
        :label="`Select duration`"
        :selections="durationSelections"
        v-model="duration"
        data-testid="duration"
      />

      <div class="flex justify-center items-center my-4">
        <Button variant="outline" @click="onBreakdown()" :disabled="!isAmountValid" data-testid="paymentPlanBreakdown">
          Payment breakdown <ChevronRight />
        </Button>
      </div>
      <div>
        <div class="flex text-left px-2 gap-2 items-center">
          <CheckboxComponent v-model="tosAccepted" name="acceptTermsAndConditions" id="terms-checkbox" />
          <label class="break-words text-sm text-gray-500" for="terms-checkbox">
            You agree to
            <a
              href="#"
              class="text-brand-800 underline hover:text-brand-500"
              color="text-brand-700"
              target="_blank"
              @click="onDownloadPrivacyTerms(termsURL)"
              >LayUp's</a
            >
            terms and conditions.
          </label>
        </div>

        <div class="pt-2">
          <button
            @click="onAccept()"
            variant="outline-secondary"
            class="bg-brand-600 rounded w-full h-10 text-base-white hover:bg-brand-700 disabled:opacity-60 disabled:bg-brand-600 flex justify-center items-center"
            :disabled="!billingDay || !duration || !tosAccepted || !isAmountValid || isSubmitting"
          >
            <LayupSpinner v-if="isSubmitting" class="mr-2" />
            {{ isSubmitting ? 'Creating...' : 'Accept' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SelectInput from '@/components/common/SelectInput.vue';
import WidgetMerchantLogo from '@/components/common/WidgetMerchantLogo.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { BOOST_REWARDS_MAX_LENGTH, BOOST_TERMS_AND_CONDITIONS_URL } from '@/config';
import type { IOrder } from '@/models/orders';
import type { IPaymentPlan } from '@/models/paymentPlans';
import * as OrderService from '@/services/orders';
import * as PaymentPlanService from '@/services/paymentPlans';
import { useUserStore } from '@/stores/users';
import { useWidgetStore } from '@/stores/widget';
import { getEndDateMax, getEndDateMin } from '@/utils/boost.utils';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { determineBillingDay } from '@/utils/paymentPlan.utils';
import { nthNumber } from '@/utils/text.utils';
import { ChevronRight } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { ModelPaymentPlanPreviewRequestDepositTypeEnum } from '@layup/api-sdk';
import { useBoostStoreModal } from './useBoostStoreModal';

const { goToStep, storeId } = useBoostStoreModal();
const widgetStore = useWidgetStore();
const userStore = useUserStore();

const errorMessage = ref('');
const isLoading = ref(false);
const isSubmitting = ref(false);
const tosAccepted = ref(false);
const termsURL = BOOST_TERMS_AND_CONDITIONS_URL;
const billingDay = ref(0);
const duration = ref(0);
const isAmountValid = ref(false);
const durationSelections = ref([
  { value: 3, text: '3 months' },
  { value: 6, text: '6 months' },
  { value: 9, text: '9 months' },
  { value: 12, text: '12 months' },
]);

const { boost } = storeToRefs(widgetStore);
const voucherSettings = computed(() => boost.value?.merchant.voucherSettings);
const savingsGoal = ref(0);
const rewardType = computed(() => voucherSettings.value?.rewardType ?? null);
const isRewardPercentage = computed(() => rewardType.value === 'PERCENTAGE');

const rewardAmount = computed(() => {
  if (isRewardPercentage.value) {
    return (savingsGoal.value / 100) * widgetStore.boost.merchant.voucherSettings?.rewardPercentage!;
  }
  return widgetStore.boost.merchant.voucherSettings?.rewardFlatAmountInCents;
});

const monthlyBillingDays = computed(() => {
  return Array.from(Array(28).keys()).map((i) => {
    return { value: i + 1, text: `${i + 1} ` + nthNumber(i + 1) };
  });
});

watch(duration, (newValue) => {
  widgetStore.boost.newBoost.duration = parseInt(newValue.toString());
});

watch(billingDay, (newValue) => {
  widgetStore.boost.newBoost.billingDay = parseInt(newValue.toString());
});

watch(savingsGoal, async (newValue) => {
  if (
    newValue >= boost.value?.merchant.voucherSettings?.minimumSavingGoalInCents! &&
    newValue <= boost.value?.merchant.voucherSettings?.maximumSavingGoalInCents!
  ) {
    widgetStore.amountDue = newValue;
  }
});

const init = async () => {
  isLoading.value = true;

  try {
    widgetStore.boost.newBoost.deposit = 0;
    const id = storeId.value;

    widgetStore.boost.merchant = await widgetStore.getBoostMerchant(id);

    duration.value = widgetStore.boost.newBoost.duration || Number(BOOST_REWARDS_MAX_LENGTH);
    billingDay.value = determineBillingDay(widgetStore.boost.newBoost.billingDay);
    savingsGoal.value =
      widgetStore.boost.newBoost.savingsGoal || widgetStore.boost.merchant.voucherSettings?.minimumSavingGoalInCents!;
  } catch (error: any) {
    createException(error);
    errorMessage.value = 'Could not fetch order details. Please refresh to try again.';
  }

  isLoading.value = false;
};

const onBreakdown = () => {
  widgetStore.boost.newBoost.duration = duration.value;
  widgetStore.boost.newBoost.billingDay = billingDay.value;
  widgetStore.boost.newBoost.savingsGoal = savingsGoal.value;

  goToStep('breakdown');
};

const minimumSavingsGoal = computed(() => boost.value?.merchant?.voucherSettings?.minimumSavingGoalInCents ?? 0);

const onAccept = async () => {
  if (minimumSavingsGoal.value && savingsGoal.value < minimumSavingsGoal.value) {
    errorMessage.value = `Savings goal must be at least ${displayCurrency(minimumSavingsGoal.value)}.`;
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    const partialOrder: Partial<IOrder> = {
      amountDue: savingsGoal.value,
      endDateMin: getEndDateMin(),
      endDateMax: getEndDateMax(billingDay.value),
      products: [
        {
          amount: savingsGoal.value,
          sku: 'boost',
          name: 'Boost Rewards',
        },
      ],
      state: 'PARTIAL',
      depositType: ModelPaymentPlanPreviewRequestDepositTypeEnum.Flat,
      depositAmount: 100,
      depositPerc: 0,
      imageUrl: widgetStore.boost.merchant?.customFeatures?.imageUrl?.value!,
      groupPaymentPlan: { enabled: false, cloneLimit: 0, expiry: 0 },
      name: `${widgetStore.boost.merchant?.name}: Boost Rewards`,
      type: 'REWARDS',
      merchantId: widgetStore.boost.merchant?._id?.toString(),
    };

    const order = await OrderService.createOrder(partialOrder);
    widgetStore.order = order;
    widgetStore.boost.partialOrder = {};

    const paymentPlan = await PaymentPlanService.createPaymentPlan({
      orderId: order._id,
      benefactorId: userStore.user._id!,
      quantity: duration.value,
      automaticBilling: true,
      offset: billingDay.value - 1,
      frequency: 'MONTHLY',
    } as any);

    widgetStore.plan = paymentPlan as IPaymentPlan;
    widgetStore.amountDue = paymentPlan.depositDue ? paymentPlan.depositDue : 0;
    widgetStore.id = paymentPlan._id ? paymentPlan._id : '';
    widgetStore.payAtReference = paymentPlan.payAtReferenceNumber || 'not found';

    const fullOrder = await OrderService.getOrderByIdWithPlans(order._id);
    widgetStore.order = fullOrder;
    if (fullOrder.plans?.[0]) {
      widgetStore.plan = fullOrder.plans[0] as IPaymentPlan;
    }

    addMetric('boost_store_order_created', {
      channel: 'shopper',
      storeId: storeId.value,
      savingsGoal: savingsGoal.value,
      duration: duration.value,
    });

    if (!userStore.user.email && !userStore.user.verifiedEmail) {
      goToStep('enter-email');
    } else {
      goToStep('contribute');
    }
  } catch (error: any) {
    createException(error);
    addMetric('boost_store_order_created', { status: 'error', channel: 'shopper', storeId: storeId.value });
    errorMessage.value = 'Something went wrong creating your plan. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const onDownloadPrivacyTerms = (termsURL?: string) => {
  window.open(termsURL, '_blank');
};

init();
</script>
